/**
 * 供应商测试弹窗的状态与发送逻辑（旧 AIProviderView.vue 中的测试部分）：
 * 流式测试逐段追加内容；普通测试一次拿到完整结果；每次测试记入最近 10 条历史。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { aiProvidersApi } from '@/api/services/aiProviders'
import { useAuthStore } from '@/stores/auth'
import { openTestStream, readSseStream, type StreamEvent } from './streamTest'
import type { AIProvider, PerformanceMetrics, TestHistoryItem } from './types'

const MAX_HISTORY = 10

let historySeq = 0
function nextHistoryId() {
  historySeq += 1
  return `${Date.now()}-${historySeq}`
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError'
}

export function useProviderTesting() {
  const [response, setResponse] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [metrics, setMetrics] = useState<PerformanceMetrics | null>(null)
  const [testing, setTesting] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [history, setHistory] = useState<TestHistoryItem[]>([])
  const abortRef = useRef<AbortController | null>(null)

  // 页面卸载时中止还在进行的流式请求
  useEffect(() => () => abortRef.current?.abort(), [])

  const addHistory = useCallback(
    (provider: AIProvider, success: boolean, text: string, responseTime: number, result: PerformanceMetrics | null) => {
      const item: TestHistoryItem = {
        id: nextHistoryId(),
        provider_name: provider.provider_name,
        model_name: provider.model_name,
        test_time: new Date().toLocaleString('zh-CN'),
        success,
        response_time: responseTime,
        message: text,
        ttfb: result?.ttfb,
        total_chars: result?.totalChars,
        chars_per_sec: result?.charsPerSec
      }
      setHistory((current) => [item, ...current].slice(0, MAX_HISTORY))
    },
    []
  )

  const runNormalTest = async (provider: AIProvider, testMessage: string) => {
    const startTime = Date.now()
    try {
      const result = await aiProvidersApi.test(provider.id, testMessage)
      const totalTime = (Date.now() - startTime) / 1000
      const payload = (result.data as any) ?? {}
      const data = payload.data ?? payload
      const responseText: string = data.response ?? ''
      if (data.success ?? payload.success) {
        // 普通测试拿不到首字时间，不再像旧版那样用总时间的 30% 冒充
        const testMetrics: PerformanceMetrics = {
          ttfb: null,
          totalTime,
          totalChars: responseText.length,
          charsPerSec: responseText.length / (totalTime || 1)
        }
        setResponse(responseText)
        setMetrics(testMetrics)
        addHistory(provider, true, responseText, totalTime, testMetrics)
      } else {
        const message: string = data.error || data.message || '未知错误'
        setError(message)
        addHistory(provider, false, message, totalTime, null)
      }
    } catch (error_) {
      const message = errorText(error_)
      setError(message)
      addHistory(provider, false, message, 0, null)
    }
  }

  const runStreamTest = async (provider: AIProvider, testMessage: string) => {
    const controller = new AbortController()
    abortRef.current = controller
    let received = ''
    let failed = false
    const handleEvent = (event: StreamEvent) => {
      switch (event.type) {
        case 'ttfb':
          setMetrics((current) => current ?? { ttfb: event.time, totalTime: 0, totalChars: 0, charsPerSec: 0 })
          break
        case 'content':
          received += event.content
          setResponse(received)
          break
        case 'complete': {
          const final: PerformanceMetrics = {
            ttfb: event.metrics.ttfb,
            totalTime: event.metrics.total_time,
            totalChars: event.metrics.total_chars,
            charsPerSec: event.metrics.chars_per_sec
          }
          setMetrics(final)
          // 出错后后端仍会发 complete，这时不再记一条成功
          if (!failed) addHistory(provider, true, received, event.metrics.total_time, final)
          break
        }
        case 'error':
          // 保留已收到的内容，错误单独提示
          failed = true
          setError(event.error)
          addHistory(provider, false, event.error, 0, null)
          break
        default:
          break
      }
    }
    try {
      const reader = await openTestStream(provider.id, testMessage, useAuthStore.getState().token, controller.signal)
      await readSseStream(reader, handleEvent)
    } catch (error_) {
      if (isAbortError(error_)) return
      const message = `连接错误: ${errorText(error_)}`
      setError(message)
      addHistory(provider, false, message, 0, null)
    } finally {
      if (abortRef.current === controller) abortRef.current = null
    }
  }

  const sendTest = async (provider: AIProvider, testMessage: string, stream: boolean) => {
    setResponse('')
    setError(null)
    setMetrics(null)
    setTesting(true)
    setIsStreaming(stream)
    try {
      await (stream ? runStreamTest(provider, testMessage) : runNormalTest(provider, testMessage))
    } finally {
      setTesting(false)
      setIsStreaming(false)
    }
  }

  // 清空本次结果；关闭弹窗时一并中止进行中的流式请求
  const reset = useCallback(() => {
    abortRef.current?.abort()
    setResponse('')
    setError(null)
    setMetrics(null)
  }, [])

  return {
    response,
    error,
    metrics,
    testing,
    isStreaming,
    history,
    sendTest,
    reset,
    clearHistory: () => setHistory([])
  }
}
